import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import DOMPurify from 'dompurify';
import hljs from 'highlight.js';
import { marked } from 'marked';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import sanitizeHtml from 'sanitize-html';

import { replaceEmojisWithIcons } from '@/components/markdown/emojiIconMap';
import { ImageModal } from '@/components/markdown/ImageModal';
import { Skeleton } from '@/components/ui/skeleton';
import { useAxios } from '@/hooks/useAxios';
import { useQuery } from '@/utils/api';
import { cn } from '@/utils/classname';
import { processBrTags } from '@/utils/markdown.util';

export const AppMediaStoragePrefix = '#file/';

/** UTF-8 safe base64 encode */
function utf8ToBase64(str: string): string {
  const bytes = new TextEncoder().encode(str);
  const binary = Array.from(bytes, byte => String.fromCodePoint(byte)).join('');
  return btoa(binary);
}

/** UTF-8 safe base64 decode */
function base64ToUtf8(base64: string): string {
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, char => char.codePointAt(0)!);
  return new TextDecoder().decode(bytes);
}

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function convertAsciiTableToHtml(text: string): string | null {
  const lines = text.trim().split('\n');
  if (lines.length < 3) return null;

  const pipeTableRegex = /^\s*\|[^|]+\|.+\|\s*$/;
  const separatorRegex = /^\s*\|[\s:]*[-=]+[\s:]*(\|[\s:]*[-=]+[\s:]*)+\|\s*$/;

  const isPipeTable = lines.every(
    line => pipeTableRegex.test(line) || separatorRegex.test(line)
  );
  if (!isPipeTable) return null;

  const parseRow = (line: string): string[] =>
    line
      .split('|')
      .slice(1, -1)
      .map(cell => cell.trim());

  const separatorIndex = lines.findIndex(line => separatorRegex.test(line));
  if (separatorIndex <= 0) return null;

  let headerRows: string[][] = [];
  let bodyRows: string[][] = [];

  headerRows = lines.slice(0, separatorIndex).map(parseRow);
  bodyRows = lines
    .slice(separatorIndex + 1)
    .filter(line => !separatorRegex.test(line))
    .map(parseRow);

  if (headerRows.length === 0 && bodyRows.length === 0) return null;

  let html = '<table>';
  if (headerRows.length > 0) {
    html += '<thead>';
    for (const row of headerRows) {
      html +=
        '<tr>' +
        row.map(cell => `<th>${escapeHtml(cell)}</th>`).join('') +
        '</tr>';
    }
    html += '</thead>';
  }
  if (bodyRows.length > 0) {
    html += '<tbody>';
    for (const row of bodyRows) {
      html +=
        '<tr>' +
        row.map(cell => `<td>${escapeHtml(cell)}</td>`).join('') +
        '</tr>';
    }
    html += '</tbody>';
  }
  html += '</table>';
  return html;
}

// Create custom renderer to handle code highlighting
const renderer = new marked.Renderer();
const originalCodeRenderer = renderer.code.bind(renderer);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
renderer.code = function (token: any) {
  const { text, lang } = token;

  // Render mermaid code blocks as diagram placeholders
  if (lang === 'mermaid') {
    const encodedSource = utf8ToBase64(text);
    return `<div class="mermaid-diagram" data-mermaid-source="${encodedSource}"></div>`;
  }

  // Always apply our custom styling, even for plain code blocks
  const validLanguage = lang && hljs.getLanguage(lang) ? lang : 'plaintext';

  // Check if this is a pipe-delimited ASCII table
  if (validLanguage === 'plaintext') {
    const tableHtml = convertAsciiTableToHtml(text);
    if (tableHtml) {
      return tableHtml;
    }
  }

  // Detect ASCII diagrams with box-drawing characters
  const boxDrawingRegex = /[┌┐└┘├┤┬┴┼─│╔╗╚╝╠╣╦╩╬═║]/;
  if (validLanguage === 'plaintext' && boxDrawingRegex.test(text)) {
    const escaped = escapeHtml(text);
    return `<div class="ascii-diagram"><pre><code style="font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,'Liberation Mono','Courier New',monospace">${escaped}</code></pre></div>`;
  }

  try {
    let highlighted = hljs.highlight(text, {
      language: validLanguage,
      ignoreIllegals: true,
    }).value;

    // If it's plaintext, apply custom pattern-based highlighting for attack chains
    if (
      validLanguage === 'plaintext' &&
      (text.includes('←') || text.includes('→') || text.includes('Priority'))
    ) {
      highlighted = escapeHtml(text)
        // Hostnames and IPs in brackets
        .replace(
          /\[([a-z0-9.-]+\.[a-z]+:\d+)\]/gi,
          '<span class="syntax-string">[$1]</span>'
        )
        .replace(
          /\[([a-z0-9.-]+)\]/gi,
          '<span class="syntax-string">[$1]</span>'
        )

        // CVE identifiers
        .replace(/(CVE-\d{4}-\d+)/g, '<span class="syntax-keyword">$1</span>')

        // Priority indicators
        .replace(/(Priority \d+)/g, '<span class="syntax-number">$1</span>')

        // Attack indicators with arrows
        .replace(/←/g, '<span class="syntax-operator">←</span>')
        .replace(/→/g, '<span class="syntax-operator">→</span>')
        .replace(/↓/g, '<span class="syntax-operator">↓</span>')

        // Status indicators in brackets
        .replace(
          /\[(Compromised|Credential Harvesting|Data Exfiltration|Hardware Control|Application Compromise)\]/g,
          '<span class="syntax-deletion">[$1]</span>'
        )

        // Special terms
        .replace(
          /\b(INTERNET|CRITICAL)\b/g,
          '<span class="syntax-keyword">$1</span>'
        );
    }

    // Comprehensive token mapping for maximum color differentiation
    const colorized = highlighted
      // Strings and text
      .replace(/class="hljs-string"/g, 'class="hljs-string syntax-string"')
      .replace(/class="hljs-attr"/g, 'class="hljs-attr syntax-attr"')
      .replace(/class="hljs-attribute"/g, 'class="hljs-attribute syntax-attr"')
      .replace(/class="hljs-regexp"/g, 'class="hljs-regexp syntax-regex"')

      // Keywords and control flow
      .replace(/class="hljs-keyword"/g, 'class="hljs-keyword syntax-keyword"')
      .replace(/class="hljs-literal"/g, 'class="hljs-literal syntax-literal"')
      .replace(/class="hljs-built_in"/g, 'class="hljs-built_in syntax-builtin"')

      // Numbers and constants
      .replace(/class="hljs-number"/g, 'class="hljs-number syntax-number"')

      // Functions and methods
      .replace(/class="hljs-title"/g, 'class="hljs-title syntax-function"')
      .replace(
        /class="hljs-function"/g,
        'class="hljs-function syntax-function"'
      )
      .replace(/class="hljs-name"/g, 'class="hljs-name syntax-name"')

      // Variables and parameters
      .replace(
        /class="hljs-variable"/g,
        'class="hljs-variable syntax-variable"'
      )
      .replace(/class="hljs-params"/g, 'class="hljs-params syntax-param"')
      .replace(
        /class="hljs-property"/g,
        'class="hljs-property syntax-property"'
      )

      // Types and classes
      .replace(/class="hljs-type"/g, 'class="hljs-type syntax-type"')
      .replace(/class="hljs-class"/g, 'class="hljs-class syntax-class"')

      // Comments
      .replace(/class="hljs-comment"/g, 'class="hljs-comment syntax-comment"')
      .replace(/class="hljs-doctag"/g, 'class="hljs-doctag syntax-doctag"')

      // Operators and punctuation
      .replace(
        /class="hljs-operator"/g,
        'class="hljs-operator syntax-operator"'
      )
      .replace(
        /class="hljs-punctuation"/g,
        'class="hljs-punctuation syntax-punctuation"'
      )

      // Meta and special
      .replace(/class="hljs-meta"/g, 'class="hljs-meta syntax-meta"')
      .replace(
        /class="hljs-selector-tag"/g,
        'class="hljs-selector-tag syntax-tag"'
      )
      .replace(
        /class="hljs-selector-class"/g,
        'class="hljs-selector-class syntax-selector"'
      )
      .replace(
        /class="hljs-selector-id"/g,
        'class="hljs-selector-id syntax-selector"'
      )

      // Additions and deletions (for diffs)
      .replace(
        /class="hljs-addition"/g,
        'class="hljs-addition syntax-addition"'
      )
      .replace(
        /class="hljs-deletion"/g,
        'class="hljs-deletion syntax-deletion"'
      )

      // Template and interpolation
      .replace(
        /class="hljs-template-variable"/g,
        'class="hljs-template-variable syntax-template"'
      )
      .replace(/class="hljs-subst"/g, 'class="hljs-subst syntax-template"');

    // Base64 encode the code to prevent any HTML parsing issues
    const encodedCode = utf8ToBase64(text);

    return `<pre style="position: relative;"><code class="hljs language-${lang} syntax-highlighted">${colorized}</code><button class="code-copy-btn" data-code-base64="${encodedCode}" title="Copy code"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path></svg></button></pre>`;
  } catch (error) {
    console.error('Highlight.js error:', error);
    return originalCodeRenderer(token);
  }
};

// Custom renderer for inline code with copy button
// eslint-disable-next-line @typescript-eslint/no-explicit-any
renderer.codespan = function (token: any) {
  const { text } = token;
  const encodedCode = utf8ToBase64(text);
  const escaped = escapeHtml(text);

  return `<span class="inline-code-wrapper"><code>${escaped}</code><button class="inline-code-copy-btn" data-code-base64="${encodedCode}" title="Copy"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path></svg></button></span>`;
};

function escapeRawTextElements(html: string): string {
  return html.replace(/<(\/?)(script|style)([\s>/]|$)/gi, '&lt;$1$2$3');
}

const markedOptions /* : MarkedOptions */ = {
  renderer,
  pedantic: false,
  gfm: true,
  breaks: false,
  sanitize: false,
  smartypants: false,
  xhtml: false,
};
marked.setOptions(markedOptions);

const sanitizeConfig: sanitizeHtml.IOptions = {
  // NOTE: 'img' is intentionally NOT in allowedTags. Finding content
  // (definition/overview/evidence/proof) routinely contains example payloads
  // like `<img src=x onerror=...>` as documentation — these must render as
  // literal text, not as live elements. With disallowedTagsMode: 'escape', any
  // raw <img> in the markdown body is escaped. A live <img> would also fire an
  // on-render request to an attacker-controlled src (no-interaction beacon /
  // SSRF from the viewer's browser), so dropping it closes that vector too.
  // Legitimate evidence screenshots use the `![](#file/...)` markdown syntax,
  // which is extracted by renderContentWithImages BEFORE marked.parse and
  // rendered via <MarkdownMedia> — it never passes through this sanitize path.
  // The transformTags.img rule below still maps any raw `#file/` <img> to a
  // placeholder span for backward compatibility.
  allowedTags: sanitizeHtml.defaults.allowedTags.concat([
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'pre',
    'code',
    'span',
    'div',
    'button',
    'svg',
    'path',
    'rect',
    'polyline',
    'circle',
    'line',
  ]),
  allowedAttributes: {
    ...sanitizeHtml.defaults.allowedAttributes,
    img: ['src', 'alt', 'title', 'width', 'height', 'class'],
    a: ['href', 'name', 'target', 'class', 'rel'],
    pre: ['class', 'style'],
    code: ['class', 'style'],
    // H3: `style` intentionally NOT allowed on span — span emits no generated
    // inline style, and leaving it allowed would (a) admit attacker CSS and
    // (b) risk sanitize-html's unfiltered-passthrough footgun for a tag whose
    // merged allowedStyles rule resolved to undefined.
    span: ['class', 'data-src', 'id', 'data-code'],
    div: ['class', 'data-mermaid-source'],
    button: ['class', 'data-code', 'data-code-base64', 'title'],
    svg: [
      'width',
      'height',
      'viewBox',
      'fill',
      'stroke',
      'stroke-width',
      'stroke-linecap',
      'stroke-linejoin',
      'xmlns',
      'class',
    ],
    path: ['d'],
    rect: ['x', 'y', 'width', 'height', 'rx', 'ry'],
    polyline: ['points'],
    circle: ['cx', 'cy', 'r'],
    line: ['x1', 'y1', 'x2', 'y2'],
  },
  // Per-tag CLASS allowlist covering only what this component's renderer
  // actually generates. The previous `'*': [/.*/]` wildcard admitted every
  // class on every allowed tag — and because the app's compiled Tailwind ships
  // overlay primitives as classes (`fixed`, `inset-0`, `z-50`, `bg-*`, ...),
  // `<div class="fixed inset-0 z-50 bg-white">` achieved a full-viewport
  // UI-redress overlay without touching the style attribute at all.
  //
  // The `'*': []` catch-all is mandatory, same footgun as allowedStyles:
  // sanitize-html passes classes UNFILTERED for any tag that has `class` in
  // allowedAttributes but no covering allowedClasses rule (tag-specific or
  // '*'). The empty catch-all guarantees every tag has a covering rule, so
  // unlisted classes are dropped fail-safe.
  allowedClasses: {
    // renderer.code output: `<code class="hljs language-${lang}
    // syntax-highlighted">`. `lang` comes from the fence info string, so the
    // language- prefix is pattern-matched (anchored; charset excludes spaces
    // and quotes). No compiled Tailwind utility starts with these prefixes.
    code: ['hljs', 'syntax-highlighted', /^language-[\w#+.-]*$/],
    // highlight.js token spans (`hljs-string`, ...), our syntax-* companion
    // classes added by the colorizer, hljs 11 secondary scope classes
    // (`function_`, `class_`, `inherited__` — lowercase word + 1-2 trailing
    // underscores, a shape no Tailwind utility has), the inline-code copy
    // wrapper, and the #file/ img placeholder from transformTags.img.
    span: [
      /^hljs-[\w-]+$/,
      /^syntax-[\w-]+$/,
      /^[a-z]+_{1,2}$/,
      'inline-code-wrapper',
      'markdown-media-placeholder',
    ],
    // renderer.code diagram wrappers.
    div: ['mermaid-diagram', 'ascii-diagram'],
    // Copy-to-clipboard buttons generated by renderer.code / renderer.codespan.
    button: ['code-copy-btn', 'inline-code-copy-btn'],
    // Fail-safe: every other tag gets an (empty) covering rule — all classes
    // dropped. pre/a/svg/img generate no classes before sanitization (emoji
    // svgs and the mermaid error-fallback classes are injected AFTER it).
    '*': [],
  },
  // H3: property allowlist for inline `style` (sanitize-html value-regex
  // mechanism). This is a PROPERTY allowlist, not a value blocklist — any
  // declaration whose property is not an own-key here is DROPPED (fail-safe
  // default), so URL-bearing functions (`url()`, `image-set()`, `expression()`,
  // `@import`, `behavior:url`, `cursor:url`, `content:url`) are unrepresentable
  // because no property that accepts a URL is allowlisted at all. The `'*'`
  // rule is mandatory: it guarantees every `style`-bearing tag has a covering
  // rule, closing sanitize-html's footgun where a tag with no merged rule
  // (`selectedRule === undefined`) passes all declarations UNFILTERED.
  allowedStyles: {
    '*': {
      // Generated by the ASCII box-drawing diagram
      // (`<code style="font-family:…monospace">`). The charset excludes `(`
      // and `:`, so a `url(...)` beacon or a smuggled second declaration
      // cannot ride in this value even if the property were ever reused.
      'font-family': [/^[\w\s,'"-]+$/],
      // Generated by renderer.code for the copy-button anchor
      // (`<pre style="position: relative;">`). ONLY `relative` is admitted, so
      // `fixed`/`absolute`/`sticky` are structurally rejected — kills overlay
      // UI-redress via inline style. The parallel class-based route (Tailwind
      // ships `fixed`/`inset-0`/`z-50`/`bg-*` as compiled classes) is closed
      // by the per-tag allowedClasses allowlist above.
      position: [/^relative$/],
    },
  },
  allowedSchemes: ['http', 'https', 'ftp', 'mailto', 'tel'],
  selfClosing: [
    'img',
    'br',
    'hr',
    'area',
    'base',
    'basefont',
    'input',
    'link',
    'meta',
  ],
  disallowedTagsMode: 'escape',
  transformTags: {
    img: function (tagName, attribs) {
      if (attribs.src && attribs.src.startsWith(AppMediaStoragePrefix)) {
        const actualSrc = attribs.src.replace(AppMediaStoragePrefix, '');
        return {
          tagName: 'span',
          attribs: {
            class: 'markdown-media-placeholder',
            'data-src': actualSrc,
          },
          text: '',
        };
      }
      return { tagName, attribs };
    },
    a: function (tagName, attribs) {
      return {
        tagName: tagName,
        attribs: {
          ...attribs,
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      };
    },
  },
};

interface MarkdownMediaProps {
  src: string;
  alt?: string;
  thumbnail?: boolean;
  onClick?: () => void;
  email?: string;
}

export function MarkdownMedia({
  src,
  alt,
  thumbnail = false,
  onClick,
  email,
}: MarkdownMediaProps) {
  const axios = useAxios({ email });
  const [imageLoadError, setImageLoadError] = useState(false);

  const { data: presignedUrl, isFetching } = useQuery<string>({
    defaultErrorMessage: 'Failed to load image',
    queryKey: ['markdown-media', src, email],
    queryFn: async () => {
      const res = await axios.get<{ url: string }>('/file', {
        params: { name: src },
      });
      return res.data.url || '';
    },
    retry: 1,
    staleTime: 4 * 60 * 1000, // 4 minutes (presigned URLs typically expire in 15min)
    enabled: Boolean(src),
  });

  const hasCaption =
    alt && alt !== 'Evidence screenshot' && alt !== 'Uploaded image';

  if (isFetching) {
    return <Skeleton className="mb-2 h-5" />;
  }

  return (
    <>
      {presignedUrl && !imageLoadError ? (
        thumbnail ? (
          <figure className="w-full max-w-2xl">
            <img
              src={presignedUrl}
              onError={() => setImageLoadError(true)}
              alt={alt || 'Evidence screenshot'}
              className="border-chariot-stroke focus:ring-chariot-primary mx-auto w-full cursor-pointer rounded-lg border object-contain shadow-md transition-all duration-200 hover:scale-[1.02] hover:shadow-xl focus:ring-2 focus:outline-hidden"
              onClick={onClick}
              tabIndex={0}
              role="button"
              aria-label={`Click to view full-size ${alt || 'evidence screenshot'}`}
            />
            {hasCaption && (
              <figcaption className="text-chariot-text-secondary mt-2 text-center text-sm">
                {alt}
              </figcaption>
            )}
          </figure>
        ) : (
          <img
            src={presignedUrl}
            onError={() => setImageLoadError(true)}
            alt={alt || 'Evidence screenshot'}
            className="max-w-full rounded-lg"
          />
        )
      ) : (
        <div className="text-chariot-text-secondary flex items-center justify-center rounded">
          <QuestionMarkCircleIcon className="size-5" />
          <span className="ml-1 text-xs">Error loading image</span>
        </div>
      )}
    </>
  );
}

interface MarkdownPreviewProps {
  source?: string;
  className?: string;
  style?: React.CSSProperties;
  thumbnail?: boolean;
  processVulnerabilityContent?: boolean;
  email?: string;
}

// MediaPortalInfo interface removed - was defined but never used

export function MarkdownPreview({
  source = '',
  className,
  style,
  thumbnail = false,
  processVulnerabilityContent = false,
  email,
}: MarkdownPreviewProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);

  // Extract image sources from markdown for modal
  const imageSrcs = useMemo(() => {
    if (!source) return [];
    const imageRegex = /!\[([^\]]*)\]\(#file\/([^)]+)\)/g;
    const matches = [...source.matchAll(imageRegex)];
    return matches.map(match => match[2]); // Extract the file path
  }, [source]);

  // Extract and render images directly in JSX
  const renderContentWithImages = () => {
    if (!source) return null;

    // Split content by image references
    const parts = source.split(/!\[([^\]]*)\]\(#file\/([^)]+)\)/);
    const elements = [];

    for (let i = 0; i < parts.length; i++) {
      if (i % 3 === 0) {
        // Regular markdown content
        if (parts[i]) {
          const partHtml = marked.parse(parts[i]) as string;
          let safeHtml = sanitizeHtml(
            escapeRawTextElements(partHtml),
            sanitizeConfig
          );
          if (processVulnerabilityContent) {
            safeHtml = processBrTags(safeHtml);
          }

          safeHtml = replaceEmojisWithIcons(safeHtml);

          elements.push(
            <div
              key={`content-${i}`}
              dangerouslySetInnerHTML={{ __html: safeHtml }}
            />
          );
        }
      } else if (i % 3 === 2) {
        // Image reference - parts[i-1] is alt text, parts[i] is src
        // Supports size hint in alt text: ![alt|50%](#file/...) or ![alt|300px](#file/...)
        const rawAlt = parts[i - 1] || 'Uploaded image';
        const src = parts[i];
        const imageIdx = Math.floor(i / 3);

        let alt = rawAlt;
        let imageMaxWidth: string | undefined;
        const sizeMatch = rawAlt.match(/^(.*?)\|(\d+(?:px|%))$/);
        if (sizeMatch) {
          alt = sizeMatch[1].trim() || 'Uploaded image';
          imageMaxWidth = sizeMatch[2];
        }

        elements.push(
          <div
            key={`image-${i}`}
            className="my-6 flex max-w-full justify-center overflow-hidden"
            style={imageMaxWidth ? { maxWidth: imageMaxWidth } : undefined}
          >
            <MarkdownMedia
              src={src}
              alt={alt}
              thumbnail={thumbnail}
              email={email}
              onClick={
                thumbnail
                  ? () => {
                      setImageIndex(imageIdx);
                      setModalOpen(true);
                    }
                  : undefined
              }
            />
          </div>
        );
      }
    }

    return elements;
  };

  const contentRef = useRef<HTMLDivElement>(null);

  // Handle copy button clicks
  useEffect(() => {
    if (!contentRef.current) return;

    const handleClick = (e: Event) => {
      const target = e.target as HTMLElement;
      const btn = target.closest(
        '.code-copy-btn, .inline-code-copy-btn'
      ) as HTMLButtonElement;
      if (!btn) return;

      e.stopPropagation();
      e.preventDefault();

      // Decode base64 to get the original code
      const encodedCode = btn.dataset.codeBase64 || '';
      const code = encodedCode ? base64ToUtf8(encodedCode) : '';
      navigator.clipboard.writeText(code);

      // Change to check icon - always use 24x24 for proper scaling
      btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>`;

      // Revert after 2 seconds
      setTimeout(() => {
        btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"></rect><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"></path></svg>`;
      }, 2000);
    };

    const currentRef = contentRef.current;
    currentRef.addEventListener('click', handleClick);
    return () => currentRef?.removeEventListener('click', handleClick);
  }, []);

  // Render mermaid diagrams
  useEffect(() => {
    if (!contentRef.current) return;

    const mermaidDivs = contentRef.current.querySelectorAll(
      '.mermaid-diagram[data-mermaid-source]'
    );
    if (mermaidDivs.length === 0) return;

    let cancelled = false;

    const renderMermaid = async () => {
      const mermaid = (await import('mermaid')).default;
      mermaid.initialize({
        startOnLoad: false,
        theme: 'default',
        securityLevel: 'strict',
        fontFamily: 'inherit',
      });

      for (let i = 0; i < mermaidDivs.length; i++) {
        if (cancelled) return;
        const div = mermaidDivs[i] as HTMLElement;
        const encodedSource = div.dataset.mermaidSource || '';
        try {
          const mermaidSource = base64ToUtf8(encodedSource);
          const { svg } = await mermaid.render(
            `mermaid-${Date.now()}-${i}`,
            mermaidSource
          );
          if (!cancelled) {
            div.innerHTML = DOMPurify.sanitize(svg, {
              USE_PROFILES: { html: true, svg: true, svgFilters: true },
              ADD_TAGS: ['foreignObject'],
              FORBID_TAGS: ['style'],
            });
          }
        } catch {
          if (!cancelled) {
            // On error, show the raw source as a code block fallback
            const mermaidSource = base64ToUtf8(encodedSource);
            const pre = document.createElement('pre');
            pre.className = 'text-chariot-text-secondary text-sm';
            const code = document.createElement('code');
            code.textContent = mermaidSource;
            pre.appendChild(code);
            div.replaceChildren(pre);
          }
        }
      }
    };

    renderMermaid();

    return () => {
      cancelled = true;
    };
  }, [source]);

  return (
    <div className="markdown-preview" ref={contentRef}>
      <div
        className={cn(
          // Explicit typography styles for Tailwind v4 compatibility
          '[&_a]:underline',
          '[&_h1]:mt-6 [&_h1]:mb-4 [&_h1]:text-xl [&_h1]:font-bold',
          '[&_h2]:mt-5 [&_h2]:mb-3 [&_h2]:text-lg [&_h2]:font-bold',
          '[&_h3]:mt-4 [&_h3]:mb-2 [&_h3]:text-base [&_h3]:font-bold',
          '[&_h4]:mt-3 [&_h4]:mb-2 [&_h4]:text-sm [&_h4]:font-bold',
          '[&_p]:mb-3 [&_p]:text-sm [&_p]:leading-relaxed',
          '[&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5',
          '[&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5',
          '[&_li]:mb-1 [&_li]:text-sm',
          '[&_strong]:font-semibold',
          '[&_code]:bg-chariot-background-secondary [&_code]:rounded-md [&_code]:p-1',
          '[&_pre]:my-3 [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_pre]:rounded-lg',
          // Table styling for markdown tables
          '[&_table]:my-3 [&_table]:w-full [&_table]:border-collapse [&_table]:overflow-hidden [&_table]:rounded-lg [&_table]:text-sm',
          '[&_table]:border-chariot-stroke [&_table]:border',
          '[&_thead]:bg-chariot-layer1',
          '[&_th]:text-chariot-text-primary [&_th]:border-chariot-stroke [&_th]:border-b [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold',
          '[&_td]:text-chariot-text-secondary [&_td]:border-chariot-stroke [&_td]:border-b [&_td]:px-3 [&_td]:py-2',
          '[&_tbody_tr:last-child_td]:border-b-0',
          '[&_tbody_tr:hover]:bg-chariot-layer1/50',
          // ASCII diagram styling
          '[&_.ascii-diagram]:my-4 [&_.ascii-diagram]:flex [&_.ascii-diagram]:justify-center [&_.ascii-diagram]:overflow-x-auto',
          '[&_.ascii-diagram_pre]:bg-chariot-layer1 [&_.ascii-diagram_pre]:border-chariot-stroke [&_.ascii-diagram_pre]:rounded-lg [&_.ascii-diagram_pre]:border [&_.ascii-diagram_pre]:px-6 [&_.ascii-diagram_pre]:py-4',
          '[&_.ascii-diagram_code]:text-chariot-text-primary [&_.ascii-diagram_code]:rounded-none [&_.ascii-diagram_code]:bg-transparent [&_.ascii-diagram_code]:p-0 [&_.ascii-diagram_code]:text-sm [&_.ascii-diagram_code]:leading-snug',
          '[&_.mermaid-diagram]:my-4 [&_.mermaid-diagram]:flex [&_.mermaid-diagram]:justify-center [&_.mermaid-diagram]:overflow-x-auto',
          '[&_hr]:border-chariot-stroke [&_hr]:my-4 [&_hr]:border-t',
          'text-chariot-text-primary',
          className
        )}
        style={style}
      >
        {renderContentWithImages()}
      </div>
      {modalOpen && imageSrcs[imageIndex] && (
        <ImageModal
          open={modalOpen}
          setOpen={setModalOpen}
          imageSrcs={imageSrcs}
          imageIndex={imageIndex}
          setImageIndex={setImageIndex}
          email={email}
        />
      )}
    </div>
  );
}
